import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';

import { useOpenerDescalce } from '../api/useOpenerDescalce';
import { useOpenerDescalceSocket } from '../hooks/useOpenerDescalceSocket';
import type { OpenerDescalceRow } from '../api/useOpenerDescalce';
import {
  DEFAULT_DESCALCE_FILTERS,
  applyDescalceFilters,
  computeDescalceTotals,
  countResolvingHidden,
  type DescalceFilterState
} from '../components/descalce/descalceFilters';
import {
  DEFAULT_DESCALCE_SORT,
  sortDescalceRows,
  type DescalceSortColumn,
  type DescalceSortState
} from '../components/descalce/descalceSort';
import {
  useDescalceFreshnessFrame,
  useDescalceStale
} from '../components/descalce/descalceFreshness';
import { resolveDescalceViewState } from '../components/descalce/descalceViewState';
import { DescalceHeaderStrip } from '../components/descalce/DescalceHeaderStrip';
import { DescalceFilterBar } from '../components/descalce/DescalceFilterBar';
import { DescalceEmptyState } from '../components/descalce/DescalceEmptyState';
import {
  BeliefCell,
  DescalceCell,
  DiffCell,
  GoCell,
  GroupCell,
  RuleCell,
  SideCell,
  VenueCountCell
} from '../components/descalce/DescalceCells';

interface ColumnDef {
  key: string;
  label: string;
  sort?: DescalceSortColumn;
  align?: 'left' | 'right' | 'center';
  /**
   * Relative track weight, emitted as a PERCENTAGE of `COL_TOTAL_PX` — the
   * idiom of the other fixed tables in this module (`UnwindingPage`'s
   * `TNA_COL_WIDTHS_PX` / `TNA_COL_TOTAL_PX`, `EntryTabByPath`). Every column
   * MUST carry one: under `table-layout: fixed` a width-less column is handed
   * only what is left over, which reaches `0` on a narrow viewport.
   */
  widthPx: number;
}

/**
 * `market` carries by far the largest weight on purpose: the pair name is the
 * only unbounded text on the screen, while the numeric columns never hold more
 * than a few digits.
 */
const COLUMNS: ColumnDef[] = [
  { key: 'descalce', label: 'Descalce', sort: 'descalce', align: 'right', widthPx: 82 },
  { key: 'side', label: 'Side', widthPx: 76 },
  { key: 'group', label: 'Group', sort: 'group', widthPx: 250 },
  { key: 'market', label: 'Market', sort: 'pairName', widthPx: 700 },
  { key: 'polyYes', label: 'Poly Y', sort: 'polyYes', align: 'right', widthPx: 62 },
  { key: 'polyNo', label: 'Poly N', sort: 'polyNo', align: 'right', widthPx: 62 },
  { key: 'kalshiYes', label: 'Kalshi Y', sort: 'kalshiYes', align: 'right', widthPx: 72 },
  { key: 'kalshiNo', label: 'Kalshi N', sort: 'kalshiNo', align: 'right', widthPx: 72 },
  { key: 'belief', label: 'Belief', sort: 'belief', align: 'right', widthPx: 64 },
  { key: 'diff', label: 'Diff', sort: 'divergence', align: 'right', widthPx: 68 },
  { key: 'rule', label: 'Rule', sort: 'ruleStatus', widthPx: 88 },
  { key: 'go', label: 'Go', align: 'center', widthPx: 48 }
];

const COL_TOTAL_PX = COLUMNS.reduce((sum, col) => sum + col.widthPx, 0);

/** Narrow tracks can't afford MUI's default 16px right padding on top of a
 *  sort label's always-rendered icon, so every column but `market` trims it. */
const tightCellSx = { px: 0.75 } as const;

/**
 * Compact header, `hSx` in `UnwindingPage` being the precedent. MUI's
 * `TableSortLabel` always reserves ~22px for the sort icon even when
 * inactive, so a label is budgeted at `track - 22 - padding`; `nowrap` +
 * `ellipsis` keeps one that outgrows that budget inside its own cell instead
 * of bleeding across the border, and every header carries a `title` so the
 * full word stays reachable.
 */
const headerCellSx = {
  fontSize: '0.58rem',
  lineHeight: 1.2,
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis' as const
} as const;

const fmtTime = (ms: number): string => new Date(ms).toLocaleTimeString();

export const DescalcePage = () => {
  // Push first: every sweep the sweeper publishes lands here over the
  // `/descalce` socket and is written into the query cache below. The query
  // still owns the data — the socket only fills it faster — and its REST poll
  // backs off to a safety net for as long as frames keep arriving.
  const { pushActive } = useOpenerDescalceSocket();
  const { data, isLoading, isError, error } = useOpenerDescalce({ pushActive });

  const [filters, setFilters] = useState<DescalceFilterState>(
    DEFAULT_DESCALCE_FILTERS
  );
  const [sort, setSort] = useState<DescalceSortState>(DEFAULT_DESCALCE_SORT);

  const frame = useDescalceFreshnessFrame(data?.computedAt);
  const stale = useDescalceStale(frame, data?.staleMs);

  const viewState = resolveDescalceViewState({
    isLoading,
    isError,
    error,
    data
  });

  const visibleRows: OpenerDescalceRow[] = useMemo(() => {
    const rows = data?.rows ?? [];
    return sortDescalceRows(applyDescalceFilters(rows, filters), sort);
  }, [data, filters, sort]);

  const totals = useMemo(() => computeDescalceTotals(visibleRows), [visibleRows]);

  const resolvingHidden = useMemo(
    () => countResolvingHidden(data?.rows ?? [], filters),
    [data, filters]
  );

  const toggleSort = (column: DescalceSortColumn) => {
    setSort(prev =>
      prev.column === column
        ? { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column, direction: 'desc' }
    );
  };

  const lastSuccessLabel = frame ? fmtTime(frame.computedAt) : null;

  return (
    <Box
      sx={{
        px: 2,
        pt: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <BalanceOutlinedIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Descalces
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
          Cross-group opener leg imbalance — read-only triage, largest naked leg
          first.
        </Typography>
      </Stack>

      {stale && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1, flexShrink: 0 }}>
          Stale — last read {lastSuccessLabel ?? 'unknown'}. Showing the last
          known snapshot; this does NOT substitute another data source.
        </Alert>
      )}

      <DescalceFilterBar filters={filters} onChange={setFilters} />
      <DescalceHeaderStrip
        totals={totals}
        snapshot={data}
        resolvingHidden={resolvingHidden}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <TableContainer>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              {COLUMNS.map(col => (
                <col
                  key={col.key}
                  style={{ width: `${(col.widthPx / COL_TOTAL_PX) * 100}%` }}
                />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? 'left'}
                    title={col.label}
                    sx={{
                      ...headerCellSx,
                      ...(col.key === 'market' ? {} : tightCellSx)
                    }}
                  >
                    {col.sort ? (
                      <TableSortLabel
                        active={sort.column === col.sort}
                        direction={sort.column === col.sort ? sort.direction : 'desc'}
                        onClick={() => toggleSort(col.sort as DescalceSortColumn)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {viewState !== 'rows' ? (
                <DescalceEmptyState state={viewState} colSpan={COLUMNS.length} />
              ) : visibleRows.length === 0 ? (
                // The snapshot has rows, but the active client-side filters
                // (search / min descalce / toggles) hide all of them — a
                // distinct message from `DescalceEmptyState`'s four
                // backend-driven states, so "genuinely no positions" and
                // "filtered down to zero" never read the same.
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    align="center"
                    data-descalce-empty-state="filtered-empty"
                  >
                    <Typography sx={{ fontSize: '0.82rem', color: 'grey.500', py: 2 }}>
                      No rows match the current filters — {data?.rows.length ?? 0} row(s) in the snapshot are hidden.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map(row => (
                  <TableRow key={row.ruleId} hover data-rule-id={row.ruleId}>
                    <TableCell align="right" sx={tightCellSx}>
                      <DescalceCell row={row} />
                    </TableCell>
                    <TableCell sx={tightCellSx}>
                      <SideCell row={row} />
                    </TableCell>
                    <TableCell sx={tightCellSx}>
                      <GroupCell group={row.group} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem' }} noWrap title={row.pairName}>
                        {row.pairName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell value={row.venue?.polyYes} />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell value={row.venue?.polyNo} />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell value={row.venue?.kalshiYes} />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <VenueCountCell value={row.venue?.kalshiNo} />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <BeliefCell row={row} />
                    </TableCell>
                    <TableCell align="right" sx={tightCellSx}>
                      <DiffCell row={row} />
                    </TableCell>
                    <TableCell sx={tightCellSx}>
                      <RuleCell row={row} />
                    </TableCell>
                    <TableCell align="center" sx={tightCellSx}>
                      <GoCell row={row} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
